import React, { useEffect, useRef, useState } from 'react';
import { Lock, Send, CheckCircle2, Check, ChevronLeft, ChevronRight, Plus, AlertCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '@/services/api';
import {
  RATINGS, GUIDANCE, PARTS, DEVELOPMENT, SUMMARY_QUESTION, NEXT_GOAL_COLUMNS,
  MANAGERS, STATEMENTS, COMPANY_QUESTIONS, COLLEAGUES_QUESTION, emptyAnswers, emptyFeedback,
} from './questions';

const CARD = 'rounded-xl border border-gray-200 bg-white p-5 sm:p-6';
const BTN = 'inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50';
const inputCls = (bad) =>
  `w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 ${bad ? 'border-red-400 bg-red-50/40 focus:border-red-500 focus:ring-red-500' : 'border-gray-300 focus:border-primary-500 focus:ring-primary-500'}`;

// Marks a field the person still has to fill in
const Need = ({ show, text = 'Please fill this in' }) =>
  show ? <p className="mt-1 flex items-center gap-1 text-xs font-medium text-red-600"><AlertCircle className="h-3.5 w-3.5" />{text}</p> : null;

const Section = ({ title, note, children }) => (
  <section className={CARD}>
    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
    {note && <p className="mt-1 text-sm text-gray-500">{note}</p>}
    <div className="mt-5 space-y-5">{children}</div>
  </section>
);

const Question = ({ id, label, hint, value, onChange, readOnly, bad, rows = 3 }) => (
  <div data-field={id}>
    <label className="block text-sm font-medium text-gray-800">{label}</label>
    {hint && <p className="text-xs text-gray-500">{hint}</p>}
    {readOnly ? (
      <p className="mt-1 whitespace-pre-wrap rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">{value || 'No answer'}</p>
    ) : (
      <textarea rows={rows} value={value} onChange={(e) => onChange(e.target.value)} className={`mt-1.5 ${inputCls(bad)}`} />
    )}
    <Need show={bad} />
  </div>
);

// 1 to 5 as buttons, with the meaning of the chosen one underneath
export const RatingButtons = ({ value, onChange, readOnly, bad }) => {
  const chosen = RATINGS.find((r) => r[0] === Number(value));
  if (readOnly) return <span className="text-sm text-gray-800">{chosen ? `${chosen[0]}. ${chosen[1]}` : 'Not rated'}</span>;
  return (
    <div>
      <div className={`inline-flex gap-1 rounded-lg p-0.5 ${bad ? 'ring-1 ring-red-400' : ''}`}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            title={RATINGS.find((r) => r[0] === n)[1]}
            onClick={() => onChange(String(n))}
            className={`h-9 w-9 rounded-lg border text-sm font-semibold ${Number(value) === n ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-primary-400'}`}
          >
            {n}
          </button>
        ))}
      </div>
      <p className={`mt-1 text-xs ${chosen ? 'text-gray-600' : bad ? 'font-medium text-red-600' : 'text-gray-400'}`}>
        {chosen ? chosen[1] : bad ? 'Choose a rating' : '1 is lowest, 5 is highest'}
      </p>
    </div>
  );
};

const Choice = ({ options, value, onChange, bad }) => (
  <div>
    <div className={`inline-flex gap-1 rounded-lg p-0.5 ${bad ? 'ring-1 ring-red-400' : ''}`}>
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(o)} className={`h-9 rounded-lg border px-3 text-sm font-medium ${value === o ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-primary-400'}`}>{o}</button>
      ))}
    </div>
    <Need show={bad} text="Choose one" />
  </div>
);

// Goal rows: start with one, add up to the limit. Each row is a small card.
const GoalRows = ({ part, columns, rows, onChange, readOnly, errors }) => {
  const filled = rows.reduce((last, r, i) => (Object.values(r).some(Boolean) ? i + 1 : last), 0);
  const [shown, setShown] = useState(Math.max(1, filled));
  const count = readOnly ? filled : Math.max(shown, filled, 1);
  if (readOnly && !filled) return <p className="text-sm text-gray-500">None given</p>;

  return (
    <div className="space-y-3">
      {errors.has(`${part}.any`) && <p data-field={`${part}.any`} className="flex items-center gap-1 text-sm font-medium text-red-600"><AlertCircle className="h-4 w-4" />Add at least one goal</p>}
      {rows.slice(0, count).map((row, i) => (
        <div key={i} className="rounded-lg border border-gray-200 bg-gray-50/60 p-4">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Goal {i + 1}</p>
          <div className="grid gap-4 md:grid-cols-2">
            {columns.map(([field, label, kind]) => {
              const key = `${part}.${i}.${field}`;
              const bad = errors.has(key);
              return (
                <div key={field} data-field={key} className={kind ? '' : field === 'goal' ? 'md:col-span-2' : ''}>
                  <label className="block text-sm font-medium text-gray-700">{label}</label>
                  <div className="mt-1.5">
                    {kind === 'rating' ? (
                      <RatingButtons value={row[field]} readOnly={readOnly} bad={bad} onChange={(v) => onChange(i, field, v)} />
                    ) : kind === 'yesno' ? (
                      readOnly ? <span className="text-sm text-gray-800">{row[field] || 'No answer'}</span> : <Choice options={['Yes', 'Partly', 'No']} value={row[field]} bad={bad} onChange={(v) => onChange(i, field, v)} />
                    ) : readOnly ? (
                      <p className="whitespace-pre-wrap text-sm text-gray-800">{row[field] || 'No answer'}</p>
                    ) : (
                      <>
                        <textarea rows={2} value={row[field]} onChange={(e) => onChange(i, field, e.target.value)} className={inputCls(bad)} />
                        <Need show={bad} />
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {!readOnly && count < rows.length && (
        <button type="button" onClick={() => setShown(count + 1)} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline">
          <Plus className="h-4 w-4" />Add another goal
        </button>
      )}
    </div>
  );
};

// What still needs an answer in the named part, as field keys
export const appraisalIssues = (a) => {
  const out = new Set();
  const rowsCheck = (part, cols) => {
    const started = a[part].filter((r) => Object.values(r).some(Boolean));
    if (!started.length) out.add(`${part}.any`);
    a[part].forEach((r, i) => {
      if (!Object.values(r).some(Boolean)) return;
      cols.forEach(([f]) => { if (!r[f]) out.add(`${part}.${i}.${f}`); });
    });
  };
  const qCheck = (key, list) => list.forEach(([f]) => { if (!String(a[key][f] || '').trim()) out.add(`${key}.${f}`); });
  Object.entries(PARTS).forEach(([part, p]) => { rowsCheck(part, p.columns); qCheck(p.questionsKey, p.questions); });
  qCheck('development', DEVELOPMENT);
  if (!a.summary.trim()) out.add('summary');
  rowsCheck('next_goals', NEXT_GOAL_COLUMNS);
  return out;
};

const STEPS = [
  { id: 'personal', label: 'Personal goals', keys: ['personal', 'personal_q'] },
  { id: 'department', label: 'Department goals', keys: ['department', 'department_q'] },
  { id: 'company', label: 'Company goals', keys: ['company', 'company_q'] },
  { id: 'development', label: 'Development', keys: ['development'] },
  { id: 'summary', label: 'Summary', keys: ['summary', 'next_goals'] },
];
const stepOf = (key, steps) => steps.findIndex((s) => s.keys.includes(key.split('.')[0]));

// Step chips with ticks, and a bar for how far along the person is
const Stepper = ({ steps, step, setStep, issuesByStep, showErrors, progress }) => (
  <div className={`${CARD} space-y-4 !p-4`}>
    <div className="flex items-center justify-between text-sm">
      <span className="font-medium text-gray-900">Step {step + 1} of {steps.length}: {steps[step].label}</span>
      <span className="text-gray-500">{progress}% done</span>
    </div>
    <div className="h-1.5 overflow-hidden rounded-full bg-gray-100"><div className="h-full rounded-full bg-primary-600 transition-all" style={{ width: `${progress}%` }} /></div>
    <div className="flex gap-1.5 overflow-x-auto">
      {steps.map((s, i) => {
        const missing = issuesByStep[i];
        const done = !missing;
        const warn = showErrors && missing;
        return (
          <button key={s.id} type="button" onClick={() => setStep(i)} className={`inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${i === step ? 'border-primary-600 bg-primary-50 text-primary-800' : warn ? 'border-red-300 bg-red-50 text-red-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'}`}>
            <span className={`grid h-4 w-4 place-items-center rounded-full text-[10px] ${done ? 'bg-green-600 text-white' : warn ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-600'}`}>
              {done ? <Check className="h-3 w-3" /> : warn ? '!' : i + 1}
            </span>
            {s.label}
            {warn && <span>({missing})</span>}
          </button>
        );
      })}
    </div>
  </div>
);

const scrollToField = (key) =>
  setTimeout(() => {
    const el = document.querySelector(`[data-field="${key}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.querySelector('textarea, input, select, button')?.focus({ preventScroll: true });
  }, 60);

// Parts 1-4 and 6. With `steps`, only that step's part is shown.
export const AppraisalBody = ({ answers, setAnswers, readOnly, errors = new Set(), only }) => {
  const a = answers || emptyAnswers();
  const setRow = (part, i, field, v) => setAnswers({ ...a, [part]: a[part].map((r, j) => (j === i ? { ...r, [field]: v } : r)) });
  const setQ = (key, field, v) => setAnswers({ ...a, [key]: { ...a[key], [field]: v } });
  const show = (id) => !only || only === id;

  return (
    <>
      {Object.entries(PARTS).map(([part, p]) => show(part) && (
        <Section key={part} title={p.title} note={p.note}>
          <GoalRows part={part} columns={p.columns} rows={a[part]} readOnly={readOnly} errors={errors} onChange={(i, f, v) => setRow(part, i, f, v)} />
          {p.questions.map(([field, label]) => (
            <Question key={field} id={`${p.questionsKey}.${field}`} label={label} value={a[p.questionsKey][field]} readOnly={readOnly} bad={errors.has(`${p.questionsKey}.${field}`)} onChange={(v) => setQ(p.questionsKey, field, v)} />
          ))}
        </Section>
      ))}
      {show('development') && (
        <Section title="Part 4. My personal development">
          {DEVELOPMENT.map(([field, label]) => (
            <Question key={field} id={`development.${field}`} label={label} value={a.development[field]} readOnly={readOnly} bad={errors.has(`development.${field}`)} onChange={(v) => setQ('development', field, v)} />
          ))}
        </Section>
      )}
      {show('summary') && (
        <Section title="Part 6. Summary">
          <Question id="summary" label={SUMMARY_QUESTION} value={a.summary} rows={5} readOnly={readOnly} bad={errors.has('summary')} onChange={(v) => setAnswers({ ...a, summary: v })} />
          <div>
            <p className="mb-2 text-sm font-medium text-gray-800">My goals for next year</p>
            <GoalRows part="next_goals" columns={NEXT_GOAL_COLUMNS} rows={a.next_goals} readOnly={readOnly} errors={errors} onChange={(i, f, v) => setRow('next_goals', i, f, v)} />
          </div>
        </Section>
      )}
    </>
  );
};

export const DetailsCard = ({ details, year }) => (
  <section className={CARD}>
    <dl className="grid gap-3 text-sm sm:grid-cols-3">
      {[['Name', details?.name], ['Department', details?.department], ['Job title', details?.job_title], ['Line manager', details?.line_manager], ['Review period', year]].map(([k, v]) => (
        <div key={k}><dt className="text-gray-500">{k}</dt><dd className="font-medium text-gray-900">{v || 'Not set in HR'}</dd></div>
      ))}
    </dl>
  </section>
);

export const RatingScale = () => (
  <details className={`${CARD} !p-4`}>
    <summary className="cursor-pointer text-sm font-medium text-gray-800">How to fill this in, and what the ratings mean</summary>
    <ul className="mb-4 mt-3 list-disc space-y-1 pl-5 text-sm text-gray-600">{GUIDANCE.map((g) => <li key={g}>{g}</li>)}</ul>
    <div className="grid gap-2 sm:grid-cols-5">
      {RATINGS.map(([n, label, meaning]) => (
        <div key={n} className="rounded-lg bg-gray-50 p-3">
          <p className="text-sm font-semibold text-gray-900">{n}. {label}</p>
          <p className="mt-0.5 text-xs text-gray-500">{meaning}</p>
        </div>
      ))}
    </div>
  </details>
);

// Bottom bar: back, next or submit, and the save status
const Footer = ({ step, last, onBack, onNext, onSubmit, busy, status, submitLabel }) => (
  <div className="sticky bottom-0 z-10 flex items-center gap-2 border-t border-gray-200 bg-white/95 py-3 backdrop-blur">
    <button type="button" disabled={step === 0 || busy} onClick={onBack} className={`${BTN} border border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}><ChevronLeft className="h-4 w-4" /><span className="hidden sm:inline">Back</span></button>
    <span className="min-w-0 flex-1 truncate text-center text-xs text-gray-500">{status}</span>
    {last ? (
      <button type="button" disabled={busy} onClick={onSubmit} className={`${BTN} bg-primary-600 text-white hover:bg-primary-700`}><Send className="h-4 w-4" />{submitLabel}</button>
    ) : (
      <button type="button" disabled={busy} onClick={onNext} className={`${BTN} bg-primary-600 text-white hover:bg-primary-700`}>Next<ChevronRight className="h-4 w-4" /></button>
    )}
  </div>
);

// The named part: one step at a time, saved as a draft between steps, submitted once
export const MyAppraisal = ({ data, onSaved }) => {
  const submitted = data.appraisal?.status === 'submitted';
  const [answers, setAnswers] = useState(() => ({ ...emptyAnswers(), ...(data.appraisal?.answers || {}) }));
  const [step, setStepRaw] = useState(0);
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const dirty = useRef(false);
  const latest = useRef(answers);
  latest.current = answers;

  const issues = appraisalIssues(answers);
  const issuesByStep = STEPS.map((_, i) => [...issues].filter((k) => stepOf(k, STEPS) === i).length);
  const total = appraisalIssues(emptyAnswers()).size;
  const progress = Math.max(0, Math.min(100, Math.round(((total - Math.min(issues.size, total)) / total) * 100)));
  const update = (next) => { dirty.current = true; setAnswers(next); };

  const saveDraft = async () => {
    if (!dirty.current) return;
    try {
      await api.put('/appraisals/me', { answers: latest.current, submit: false });
      dirty.current = false;
      setSavedAt(new Date());
    } catch {
      toast.error("Couldn't save your draft. Check your connection.");
    }
  };
  const setStep = (i) => { saveDraft(); setStepRaw(i); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const submit = async () => {
    if (issues.size) {
      setShowErrors(true);
      const first = [...issues].sort((x, y) => stepOf(x, STEPS) - stepOf(y, STEPS))[0];
      setStepRaw(stepOf(first, STEPS));
      scrollToField(first);
      toast.error(`${issues.size} ${issues.size === 1 ? 'answer is' : 'answers are'} missing. They are marked in red.`);
      saveDraft();
      return;
    }
    try {
      setBusy(true);
      const res = await api.put('/appraisals/me', { answers, submit: true });
      toast.success(res.data.message);
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.message || "Couldn't submit your appraisal");
    } finally {
      setBusy(false);
    }
  };

  // Save what's typed if the page is closed or left
  useEffect(() => () => { saveDraft(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (submitted) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" /> Submitted on {new Date(data.appraisal.submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}. HR can read it.
        </div>
        <DetailsCard details={data.details} year={data.year} />
        <AppraisalBody answers={answers} readOnly />
      </div>
    );
  }

  const errors = showErrors ? issues : new Set();
  return (
    <div className="space-y-4">
      {step === 0 && <DetailsCard details={data.details} year={data.year} />}
      {step === 0 && <RatingScale />}
      <Stepper steps={STEPS} step={step} setStep={setStep} issuesByStep={issuesByStep} showErrors={showErrors} progress={progress} />
      <AppraisalBody answers={answers} setAnswers={update} errors={errors} only={STEPS[step].id} />
      <Footer
        step={step}
        last={step === STEPS.length - 1}
        busy={busy}
        onBack={() => setStep(step - 1)}
        onNext={() => setStep(step + 1)}
        onSubmit={submit}
        submitLabel="Submit appraisal"
        status={savedAt ? `Draft saved at ${savedAt.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit' })}` : 'Your answers save as a draft when you move between steps'}
      />
    </div>
  );
};

// What still needs an answer in the anonymous part
const feedbackIssues = (f) => {
  const out = new Set();
  MANAGERS.forEach(([m]) => {
    STATEMENTS.forEach(([s]) => { if (!f.managers[m].ratings[s]) out.add(`m_${m}.${s}`); });
    ['well', 'change'].forEach((k) => { if (!f.managers[m][k].trim()) out.add(`m_${m}.${k}`); });
  });
  COMPANY_QUESTIONS.forEach(([k]) => { if (!f.company[k].trim()) out.add(`company.${k}`); });
  f.colleagues.forEach((c, i) => {
    if (!c.employee_id) out.add(`colleagues.${i}.employee_id`);
    if (!c.reason.trim()) out.add(`colleagues.${i}.reason`);
  });
  return out;
};

const FEEDBACK_STEPS = [
  ...MANAGERS.map(([m, , , short]) => ({ id: m, label: short, keys: [`m_${m}`] })),
  { id: 'company', label: 'The company', keys: ['company'] },
  { id: 'colleagues', label: 'Best colleagues', keys: ['colleagues'] },
];

// The anonymous part: sent once, never linked to the person, never saved as a draft
export const AnonymousFeedback = ({ done, onSent }) => {
  const [f, setF] = useState(emptyFeedback);
  const [people, setPeople] = useState([]);
  const [step, setStepRaw] = useState(0);
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (done) return;
    api.get('/appraisals/colleagues').then((r) => setPeople(r.data?.data || [])).catch(() => setPeople([]));
  }, [done]);

  if (done) {
    return (
      <div className={`${CARD} text-center`}>
        <Lock className="mx-auto h-8 w-8 text-gray-400" />
        <p className="mt-3 font-medium text-gray-900">Your anonymous feedback has been sent</p>
        <p className="mt-1 text-sm text-gray-500">It is stored without your name, so nobody can see which answers are yours, and it can't be opened again.</p>
      </div>
    );
  }

  const issues = feedbackIssues(f);
  const errors = showErrors ? issues : new Set();
  const issuesByStep = FEEDBACK_STEPS.map((_, i) => [...issues].filter((k) => stepOf(k, FEEDBACK_STEPS) === i).length);
  const total = feedbackIssues(emptyFeedback()).size;
  const progress = Math.round(((total - issues.size) / total) * 100);
  const setStep = (i) => { setStepRaw(i); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const setManager = (m, patch) => setF({ ...f, managers: { ...f.managers, [m]: { ...f.managers[m], ...patch } } });
  const setColleague = (i, patch) => setF({ ...f, colleagues: f.colleagues.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  const taken = f.colleagues.map((c) => String(c.employee_id));
  const current = FEEDBACK_STEPS[step];

  const send = async () => {
    if (issues.size) {
      setShowErrors(true);
      const first = [...issues].sort((x, y) => stepOf(x, FEEDBACK_STEPS) - stepOf(y, FEEDBACK_STEPS))[0];
      setStepRaw(stepOf(first, FEEDBACK_STEPS));
      scrollToField(first);
      toast.error(`${issues.size} ${issues.size === 1 ? 'answer is' : 'answers are'} missing. They are marked in red.`);
      return;
    }
    try {
      setBusy(true);
      const res = await api.post('/appraisals/anonymous', { answers: f });
      toast.success(res.data.message);
      onSent();
    } catch (error) {
      toast.error(error.response?.data?.message || "Couldn't send your feedback");
    } finally {
      setBusy(false);
    }
  };

  const manager = MANAGERS.find(([m]) => m === current.id);
  return (
    <div className="space-y-4">
      {step === 0 && (
        <div className="flex gap-3 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-900">
          <Lock className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <p>This part is anonymous. It is saved without your name, and HR and management only see it combined with everyone else's. You can send it once, and it can't be edited afterwards. Nothing is saved until you send it, so keep this page open while you answer.</p>
        </div>
      )}
      <Stepper steps={FEEDBACK_STEPS} step={step} setStep={setStep} issuesByStep={issuesByStep} showErrors={showErrors} progress={progress} />

      {manager && (() => {
        const [m, title, focus, short] = manager;
        return (
          <Section title={title} note={focus}>
            {STATEMENTS.map(([s, statement]) => {
              const bad = errors.has(`m_${m}.${s}`);
              return (
                <div key={s} data-field={`m_${m}.${s}`} className={`rounded-lg border p-4 ${bad ? 'border-red-300' : 'border-gray-200'}`}>
                  <p className="text-sm font-medium text-gray-800">{statement}</p>
                  <div className="mt-2 grid gap-3 md:grid-cols-[auto_1fr] md:items-start">
                    <RatingButtons value={f.managers[m].ratings[s]} bad={bad} onChange={(v) => setManager(m, { ratings: { ...f.managers[m].ratings, [s]: v } })} />
                    <input value={f.managers[m].comments[s] || ''} onChange={(e) => setManager(m, { comments: { ...f.managers[m].comments, [s]: e.target.value } })} placeholder="Comment (optional)" className={inputCls(false)} />
                  </div>
                </div>
              );
            })}
            <Question id={`m_${m}.well`} label={`What does ${short} do well?`} value={f.managers[m].well} bad={errors.has(`m_${m}.well`)} onChange={(v) => setManager(m, { well: v })} rows={2} />
            <Question id={`m_${m}.change`} label={`What is the one thing ${short} should change?`} value={f.managers[m].change} bad={errors.has(`m_${m}.change`)} onChange={(v) => setManager(m, { change: v })} rows={2} />
          </Section>
        );
      })()}

      {current.id === 'company' && (
        <Section title="The company overall">
          {COMPANY_QUESTIONS.map(([k, label]) => (
            <Question key={k} id={`company.${k}`} label={label} value={f.company[k]} bad={errors.has(`company.${k}`)} onChange={(v) => setF({ ...f, company: { ...f.company, [k]: v } })} />
          ))}
        </Section>
      )}

      {current.id === 'colleagues' && (
        <Section title="Best colleagues" note={COLLEAGUES_QUESTION}>
          {f.colleagues.map((c, i) => {
            const badPick = errors.has(`colleagues.${i}.employee_id`);
            const badWhy = errors.has(`colleagues.${i}.reason`);
            return (
              <div key={i} className={`rounded-lg border p-4 ${badPick || badWhy ? 'border-red-300' : 'border-gray-200'}`}>
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">{['1st', '2nd', '3rd'][i]} best</p>
                <div className="grid gap-3 md:grid-cols-[1fr_1.5fr]">
                  <div data-field={`colleagues.${i}.employee_id`}>
                    <label className="block text-sm font-medium text-gray-700">Colleague</label>
                    <select value={c.employee_id} onChange={(e) => setColleague(i, { employee_id: e.target.value })} className={`mt-1.5 ${inputCls(badPick)}`}>
                      <option value="">Choose a colleague</option>
                      {people.map((p) => (
                        <option key={p.id} value={p.id} disabled={taken.includes(String(p.id)) && String(c.employee_id) !== String(p.id)}>
                          {p.name}{p.department ? `, ${p.department}` : ''}
                        </option>
                      ))}
                    </select>
                    <Need show={badPick} text="Choose a colleague" />
                  </div>
                  <div data-field={`colleagues.${i}.reason`}>
                    <label className="block text-sm font-medium text-gray-700">Why</label>
                    <textarea rows={2} value={c.reason} onChange={(e) => setColleague(i, { reason: e.target.value })} className={`mt-1.5 ${inputCls(badWhy)}`} />
                    <Need show={badWhy} />
                  </div>
                </div>
              </div>
            );
          })}
        </Section>
      )}

      <Footer
        step={step}
        last={step === FEEDBACK_STEPS.length - 1}
        busy={busy}
        onBack={() => setStep(step - 1)}
        onNext={() => setStep(step + 1)}
        onSubmit={send}
        submitLabel="Send anonymously"
        status="Anonymous. Sent only when you press Send."
      />
    </div>
  );
};
